import { useState, useRef, useCallback, useEffect } from "react";
import { setRecommended } from "../api/books";
import { getEmotionTag } from "../utils/firestoreHelpers";
import { getUserDisplayName, getBookRegisteredDate } from "../utils/format";
import TrendingModal from "../modals/TrendingModal";
import ReadingModal from "../modals/ReadingModal";
import ConstellationModal from "../modals/ConstellationModal";
import { collectConstellations } from "../data/constellations";
import EmptyPlanet from "./EmptyPlanet";
import { rememberGuestPick, readGuestPick, clearGuestPick } from "../utils/guestPick";
import { track } from "../utils/analytics";
import { countPlanetVisitors } from "../api/users";
import FeedbackButton from "../components/FeedbackButton";
import InstagramButton from "../components/InstagramButton";
import { COLOR_UNLOCK, TRAVELER_UNLOCK, FOX_UNLOCK } from "../data/constants";
import { readBooks } from "../utils/bookState";
import NotificationBell from "./NotificationBell";
import ReleaseNotesModal from "../modals/ReleaseNotesModal";
import BookPtiModal from "../modals/BookPtiModal";
import { LATEST_RELEASE } from "../data/releaseNotes";
import PlanetSection from "./PlanetSection";
import StardustSection from "./StardustSection";
import "./HomeScreen.css";

const NEWS_KEY = "muroo-seen-release";

const HomeScreen = ({ books, allBooks = [], users = [], coversLoaded, friendCount, autoFriends = [], onBookClick, onExternalBookClick, onUserClick, onAddClick, onAddWishClick, onQuickAdd, onFriendsClick, onTrailClick, onRecentClick, onMonthlyClick, onMyTab, onSaveBookType, onLogout, currentUser, guestMode, myFriends = [], reloadFriends, notifications = [], onMarkNotificationsRead, onClearNotifications, onRecommendToggle }) => {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [showBookWorld, setShowBookWorld] = useState(false);
  const [showConstellation, setShowConstellation] = useState(false);
  /** "books" | "stories" | null */
  const [showTrending, setShowTrending] = useState(false);
  const [showNews, setShowNews] = useState(false);
  const [showPti, setShowPti] = useState(false);
  /* 맨 위 소식을 아직 안 봤으면 ✦ 에 점을 찍는다. 저장이 막힌 브라우저에서는
     찍지 않는다 — 봐도 지워지지 않는 점은 거짓말이 된다. */
  const [newsUnseen, setNewsUnseen] = useState(() => {
    try { return localStorage.getItem(NEWS_KEY) !== LATEST_RELEASE; } catch { return false; }
  });
  const openNews = () => {
    setShowNews(true);
    setNewsUnseen(false);
    try { localStorage.setItem(NEWS_KEY, LATEST_RELEASE); } catch { /* 이번엔 열었으니 됐다 */ }
  };
  /* 써보기 — 그 기능이 있는 자리로 (data/releaseNotes.js 의 go) */
  const goNews = (go) => {
    setShowNews(false);
    if (go === "bookpti") setShowPti(true);
    else if (go === "constellation") setShowConstellation(true);
    else if (go === "trending") setShowTrending(true);
    else if (go === "trail") { if (onTrailClick) onTrailClick(); }
    else if (go === "profile") onUserClick("me");
    /* 앱 밖의 정적 페이지다. 아래 onMyTab 으로 흘려보내면 '내 행성의
       booklist 탭' 을 찾다가 아무 일도 일어나지 않는다. */
    else if (go === "booklist") window.open("https://muroo.kr/booklist", "_blank", "noopener,noreferrer");
    /* 목록은 HotBooks 가 제 상태로 연다. 새 소식 창이 닫힌 뒤에 부른다. */
    else if (go === "hotbooks") setTimeout(() => window.dispatchEvent(new Event("muroo:open-hotbooks")), 50);
    else if (go === "regionbooks") setTimeout(() => window.dispatchEvent(new Event("muroo:open-regionbooks")), 50);
    else if (onMyTab) onMyTab(go);
  };
  const [showReading, setShowReading] = useState(false);
  /* 게스트가 책을 누르면 가입 안내로 데려간다. 책 화면은 로그인해야
     열리는데, 여태 눌러도 아무 일이 없어서 고장으로 보였다. */
  /* 게스트로 구경하다 누른 책. 아직 한 권도 없을 때만 내민다 —
     이미 담기 시작한 사람에게는 지난 일이다. */
  const [pick, setPick] = useState(() => readGuestPick());
  /* 게스트로 보던 화면이 그대로 살아 있는 채 회원이 되는 경우가 있다.
     그때는 mount 때 읽은 값(대개 null)이 남아 있으므로 다시 읽는다. */
  useEffect(() => { setPick(readGuestPick()); }, [currentUser?.uid, guestMode]);
  const [picking, setPicking] = useState(false);
  const dropPick = useCallback(() => { clearGuestPick(); setPick(null); }, []);
  const takePick = useCallback(async () => {
    if (!pick) return;
    setPicking(true);
    try {
      await onQuickAdd?.(pick);
      /* 게스트로 본 책이 실제로 첫 책이 되었는지 — 그 다리가 통했는지를 본다 */
      track("guest_pick_used");
      clearGuestPick();
      setPick(null);
    } catch (e) {
      console.error("담기 실패:", e.message);
      setPicking(false);
    }
  }, [pick, onQuickAdd]);

  const guestCtaRef = useRef(null);
  const litTimer = useRef(null);
  const [ctaLit, setCtaLit] = useState(false);
  /* 게스트가 이 1.4초 안에 나가면 사라진 화면에 상태를 쓰게 된다 */
  useEffect(() => () => clearTimeout(litTimer.current), []);
  const showGuestCta = useCallback((book) => {
    if (book) { rememberGuestPick(book); track("guest_book_tap"); }
    const el = guestCtaRef.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
    /* 그냥 옮겨만 놓으면 화면이 왜 움직였는지 알 수 없다. 잠깐 밝혀준다. */
    setCtaLit(true);
    clearTimeout(litTimer.current);
    litTimer.current = setTimeout(() => setCtaLit(false), 1400);
  }, []);
  /** null | "color" | "traveler" | "fox" */
  const [unlockToast, setUnlockToast] = useState(null);
  /** 내 행성에 다녀간 사람 수. 0 이면 아무것도 보여주지 않는다. */
  const [visitorCount, setVisitorCount] = useState(0);

  // 잠금이 풀린 순간에만 알린다. 기기당 한 번이면 충분하다.
  // 둘이 한꺼번에 풀려 있으면(새 기기에서 처음 열 때) 더 큰 쪽인 여행자만
  // 알리고 색 알림은 본 것으로 친다 — 알림 두 개가 줄지어 뜨면 둘 다 안 읽힌다.
  const readCount = readBooks(books, currentUser?.uid).length;
  useEffect(() => {
    if (guestMode || currentUser?.isAnonymous) return;
    const uidKey = currentUser?.uid || "";
    const colorKey = `muroo-color-unlocked-${uidKey}`;
    const travelerKey = `muroo-traveler-arrived-${uidKey}`;
    const foxKey = `muroo-fox-arrived-${uidKey}`;
    let kind = null;
    try {
      if (readCount >= FOX_UNLOCK && !localStorage.getItem(foxKey)) {
        kind = "fox";
        localStorage.setItem(foxKey, "1");
        localStorage.setItem(travelerKey, "1");
        localStorage.setItem(colorKey, "1");
      } else if (readCount >= TRAVELER_UNLOCK && !localStorage.getItem(travelerKey)) {
        kind = "traveler";
        localStorage.setItem(travelerKey, "1");
        localStorage.setItem(colorKey, "1");
      } else if (books.length >= COLOR_UNLOCK && !localStorage.getItem(colorKey)) {
        kind = "color";
        localStorage.setItem(colorKey, "1");
      }
    } catch {
      return; // 저장이 막힌 브라우저에서는 알리지 않는다 (매번 뜨는 것보다 낫다)
    }
    if (!kind) return;
    setUnlockToast(kind);
    const t = setTimeout(() => setUnlockToast(null), 6000);
    return () => clearTimeout(t);
  }, [books.length, readCount, guestMode, currentUser]);
  // 내 행성 방문자 수. 주인만 보는 값이라 RLS 가 남의 것은 0 으로 돌려준다.
  useEffect(() => {
    if (guestMode || currentUser?.isAnonymous || !currentUser?.uid) return;
    let alive = true;
    countPlanetVisitors(currentUser.uid).then((n) => { if (alive) setVisitorCount(n); });
    return () => { alive = false; };
  }, [guestMode, currentUser?.uid, currentUser?.isAnonymous]);

  const booksGridRef = useRef(null);

  const uid = currentUser?.uid;

  // Long-press to toggle recommend
  const longPressRef = useRef(null);
  const longPressTriggered = useRef(false);
  const handlePointerDown = useCallback((book) => {
    longPressTriggered.current = false;
    longPressRef.current = setTimeout(async () => {
      longPressTriggered.current = true;
      if (!uid || currentUser?.isAnonymous) return;
      const isMyBook = books.some(b => b.id === book.id);
      if (!isMyBook) return;
      const isRec = book.recommendedBy && book.recommendedBy[uid];
      await setRecommended(book.id, uid, !isRec);
      if (onRecommendToggle) onRecommendToggle(book.id, !isRec);
    }, 600);
  }, [uid, currentUser, books, onRecommendToggle]);
  const handlePointerUp = useCallback((e) => {
    if (longPressRef.current) clearTimeout(longPressRef.current);
    if (longPressTriggered.current && e) {
      e.preventDefault();
    }
  }, []);

  const completedBooks = books.filter(b => b.completedBy && b.completedBy[uid]);
  /* 담아만 두고 아직 펴지 않은 책. */
  const wishBooks = books.filter(b => b.wantToReadBy && b.wantToReadBy[uid]);
  /* 몇 개를 만들 수 있는지 버튼에 보이면 눌러볼 이유가 생긴다.
     0 이면 붙이지 않는다 — '0' 은 버튼을 죽인다. */
  const readyConstellations = collectConstellations(books, uid).filter(r => r.ready).length;
  /* 읽는 중 = 완독도 아니고 읽고 싶은 책도 아닌 것. 셋이 겹치지 않는다. */
  const incompleteBooks = books.filter(b =>
    (!b.completedBy || !b.completedBy[uid]) && !(b.wantToReadBy && b.wantToReadBy[uid])
  );

  const sortByDate = (arr) => [...arr].sort((a, b) => getBookRegisteredDate(b, uid).localeCompare(getBookRegisteredDate(a, uid)));

  const baseBooks =
    activeTab === "completed" ? sortByDate(completedBooks)
    : activeTab === "wish" ? sortByDate(wishBooks)
    : activeTab === "incomplete" ? sortByDate(incompleteBooks)
    : activeTab === "stardust" ? []
    : sortByDate(books);

  const displayBooks = searchQuery.trim()
    ? books.filter(b =>
        b.title.includes(searchQuery) ||
        b.author.includes(searchQuery) ||
        b.genre.includes(searchQuery)
      )
    : baseBooks;

  const isGuest = guestMode;

  // 게스트 화면.
  // 예전에는 유저 카드(행성 + 이름 + 통계)를 보여줬는데, 프로필 이름에 실명이
  // 들어 있을 수 있어 로그인하지 않은 사람에게 노출하지 않는다.
  // 대신 "무엇이 읽히고 있는지"만 보여준다 — 책 제목과 저자는 개인정보가 아니다.
  if (isGuest) {
    const reading = (allBooks || [])
      .map((b) => {
        const registered = Object.keys(b.registeredBy || {});
        const completed = b.completedBy || {};
        return { book: b, count: registered.filter((id) => !completed[id]).length };
      })
      .filter((x) => x.count > 0);

    const shuffled = [...reading].sort(() => Math.random() - 0.5);
    const shown = shuffled.slice(0, 6);
    const remainingCount = reading.length - shown.length;

    return (
      <div className="bp-layer bp-layer-full">
        <div className="top-bar">
          <div className="logo-block">
            <div className="logo">MU<span>ROO</span></div>
            <div className="logo-tagline">책으로 연결된 우주에 참여해보세요</div>
          </div>
          <button className="btn-logout" onClick={onLogout}>나가기</button>
        </div>
        <div className="guest-content">
          <div className="guest-hero">
            <div className="guest-title">지금 펼쳐진 책들</div>
            <div className="guest-sub">이 우주 어딘가에서 누군가 읽고 있어요</div>
          </div>
          <div className="guest-books">
            {shown.map(({ book: b, count }, i) => (
              <div
                key={b.id}
                className="guest-book-card"
                style={{ animationDelay: `${i * 0.06}s` }}
                onClick={() => showGuestCta(b)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); showGuestCta(b); } }}
              >
                <div className="book-cover" style={{ background: b.bg }}>
                  {b.cover
                    ? <img src={b.cover} alt="" className="book-cover-img" />
                    : <span>📖</span>}
                </div>
                <div className="book-info">
                  <div className="book-title">{b.title}</div>
                  <div className="book-author">{b.author}</div>
                  <div className="guest-book-count">📖 {count}명이 읽는 중</div>
                </div>
              </div>
            ))}
          </div>
          {remainingCount > 0 && (
            <div className="guest-planet-more">+{remainingCount}권이 더 읽히고 있어요</div>
          )}
          <div className={ctaLit ? "guest-cta lit" : "guest-cta"} ref={guestCtaRef}>
            <div className="guest-cta-icon">🪐</div>
            <div className="guest-cta-text">나만의 행성에서<br/>나의 책을 기록하고 공유해보세요</div>
            <button className="guest-cta-btn" onClick={onLogout}>회원가입하고 시작하기</button>
          </div>
        </div>
        <FeedbackButton />
      </div>
    );
  }

  return (
    <div className="bp-layer bp-layer-full">
      <div className="top-bar">
        <div className="logo-block">
          <div className="logo">MU<span>ROO</span></div>
          <div className="logo-tagline">책으로 연결된 우주에 참여해보세요</div>
        </div>
        <div className="top-bar-actions">
          <div className="user-chip-wrap">
            <div className="user-chip" onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="avatar-sm">🌿</div>
              <span>{getUserDisplayName(currentUser)}</span>
            </div>
            {showUserMenu && (
              <div className="user-menu-dropdown">
                <button className="user-menu-item" onClick={() => { setShowUserMenu(false); onUserClick("me"); }}>🪐 내 프로필</button>
                <button className="user-menu-item" onClick={() => { setShowUserMenu(false); if (onTrailClick) onTrailClick(); }}>🚀 우주 궤적 확인하기</button>
                {/* 방침은 앱 안에서도 닿아야 한다. 정적 파일이라 새 창으로 연다 —
                    같은 창에서 열면 SPA 를 벗어나 뒤로가기 흐름이 끊긴다. */}
                <a
                  className="user-menu-item"
                  href="/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowUserMenu(false)}
                >📄 개인정보처리방침</a>
                <button className="user-menu-item user-menu-logout" onClick={() => { setShowUserMenu(false); onLogout(); }}>로그아웃</button>
              </div>
            )}
          </div>
          <NotificationBell notifications={notifications} onMarkNotificationsRead={onMarkNotificationsRead} onClearNotifications={onClearNotifications} />
        </div>
      </div>
      <div className="home-content">
        {/* 가입 직후 첫 화면에 놓는다. 아래에 두면 0 이 세 개 붙은 칸을
            다 지나야 나와서, 정작 아무것도 없는 사람이 못 본다. */}
        {!guestMode && !currentUser?.isAnonymous && books.length === 0 && pick && (
          <div className="guest-pick">
            <div className="guest-pick-text">
              {/* 긴 제목은 줄인다. 세 줄로 번지면 버튼이 화면 밖으로 밀린다. */}
              아까 보던 <b>『{pick.title.length > 22 ? pick.title.slice(0, 22) + "…" : pick.title}』</b>부터 담아볼까요?
            </div>
            <div className="guest-pick-btns">
              <button className="guest-pick-add" onClick={takePick} disabled={picking}>
                {picking ? "담는 중…" : "＋ 이 책 담기"}
              </button>
              <button className="guest-pick-skip" onClick={dropPick}>다른 책으로</button>
            </div>
          </div>
        )}
        {/* 문턱은 0권 하나뿐이다. 한 권이라도 담으면 평소 홈으로 돌아간다 —
            3권·5권에서 무언가 나타나면 왜 나타났는지 알 수 없다. */}
        {books.length === 0 ? (
          <EmptyPlanet
            allBooks={allBooks}
            otherPlanets={(users || []).filter((u) => u.id !== uid).length}
            onAddClick={onAddClick}
            onAddWishClick={onAddWishClick}
            onQuickAdd={onQuickAdd}
            onShowUserSearch={() => setShowUserSearch(true)}
            onShowBookWorld={() => setShowBookWorld(true)}
          />
        ) : (
        <PlanetSection
          books={books}
          allBooks={allBooks}
          users={users}
          uid={uid}
          completedBooks={completedBooks}
          incompleteBooks={incompleteBooks}
          friendCount={friendCount}
          visitorCount={visitorCount}
          onUserClick={onUserClick}
          onFriendsClick={onFriendsClick}
          onRecentClick={onRecentClick}
          onTrendingClick={() => setShowTrending(true)}
          onReadingClick={() => setShowReading(true)}
          onAddClick={onAddClick}
          onAddWishClick={onAddWishClick}
          canAdd={!guestMode && !currentUser?.isAnonymous}
          setActiveTab={setActiveTab}
          setSearchQuery={setSearchQuery}
          scrollToBooks={() => setTimeout(() => booksGridRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 50)}
          onShowUserSearch={() => setShowUserSearch(!showUserSearch)}
          onShowConstellation={() => setShowConstellation(true)}
          readyConstellations={readyConstellations}
          onMonthlyClick={onMonthlyClick}
          onBookPtiClick={() => setShowPti(true)}
          myType={(users || []).find((u) => u.id === uid)?.bookType || null}
        />
        )}

        <StardustSection
          users={users}
          allBooks={allBooks}
          currentUser={currentUser}
          autoFriends={autoFriends}
          myFriends={myFriends}
          reloadFriends={reloadFriends}
          onUserClick={onUserClick}
          onBookClick={onBookClick}
          onExternalBookClick={onExternalBookClick}
          showUserSearch={showUserSearch}
          setShowUserSearch={setShowUserSearch}
          showBookWorld={showBookWorld}
          setShowBookWorld={setShowBookWorld}
        />

        <div className="search-bar-wrap">
          <span className="search-bar-icon">🔍</span>
          <input
            className="search-bar"
            placeholder="내 행성에 등록된 책 검색..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-bar-clear" onClick={() => setSearchQuery("")}>✕</button>
          )}
        </div>

        {/* 감정 태그 필터 줄은 지웠다. 태그가 7종까지 나와 모바일에서
            세 줄로 접히고, 홈 화면이 그만큼 길어졌다.
            태그 자체는 책마다 붙어 있고, 취향 요약은 행성에 있다. */}
        {!searchQuery && books.length > 0 && (
          <div className="filter-group">
            <div className="tabs">
              {[["all","전체",books.length],["completed","완독",completedBooks.length],["wish","읽고 싶은 책",wishBooks.length]].map(([v, label, n]) => (
                <button key={v} className={`tab${activeTab === v ? " active" : ""}`} onClick={() => setActiveTab(v)}>
                  <span>{label}</span>
                  {/* 0 도 보여준다. 숨기면 '읽고 싶은 책' 만 숫자가 없어
                      세는 방식이 다른 것처럼 보인다. */}
                  <span className="tab-n">{n}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {searchQuery && displayBooks.length === 0 && (
          <div className="home-empty-search">
            "{searchQuery}" 검색 결과가 없어요 🔍
          </div>
        )}
        {/* 옛 빈 안내는 EmptyPlanet 이 대신한다. 맨 아래에 있어서
            정작 아무것도 없는 사람이 0 여섯 개를 지나야 봤다. */}
        <div className="books-grid" ref={booksGridRef}>
          {displayBooks.map((b, i) => (
            <div key={b.id} className={`book-card${b.wantToReadBy && b.wantToReadBy[uid] ? " is-wish" : ""}`} style={{ animationDelay: `${i * 0.06}s` }}
              onClick={() => { if (!longPressTriggered.current) onBookClick(b.id); }}
              onTouchStart={() => handlePointerDown(b)}
              onTouchEnd={(e) => handlePointerUp(e)}
              onMouseDown={() => handlePointerDown(b)}
              onMouseUp={(e) => handlePointerUp(e)}
              onMouseLeave={handlePointerUp}
              onContextMenu={e => e.preventDefault()}
            >
              <div className="book-cover" style={{ background: b.bg }}>
                {b.cover
                  ? <img src={b.cover} alt={b.title} className="book-cover-img" />
                  : coversLoaded
                    ? <span>{b.emoji || "📖"}</span>
                    : <div className="cover-skeleton" />
                }
                {(b.recommendedBy && b.recommendedBy[uid]) && <div className="book-recommend-sun">☀️</div>}
                {/* 점선만으로는 무슨 뜻인지 알 수 없다. 한 마디를 같이 둔다.
                    카드를 흐리게 하지는 않는다 — 담아둔 책은 제목이 가장
                    중요한데 그걸 흐리면 거꾸로다. */}
                {(b.wantToReadBy && b.wantToReadBy[uid]) && <div className="book-wish-badge">담아둠</div>}
              </div>
              <div className="book-info">
                <div className="book-title">{b.title}</div>
                <div className="book-author">{b.author}</div>
                {(() => { const et = getEmotionTag(b); return et ? <div className="book-emotion-tag">{et.emoji} {et.label}</div> : null; })()}
                {b._ownerName
                  ? <div className="book-owner">📡 {b._ownerName}님이 읽는 중</div>
                  : <div className="book-readers">👥 {(users || []).filter(u => (u.bookIds || []).includes(b.id)).length}명</div>
                }
              </div>
            </div>
          ))}
        </div>
      </div>

      {unlockToast && (
        <button
          className="unlock-toast"
          onClick={() => { setUnlockToast(null); if (unlockToast === "color") onUserClick("me"); else window.scrollTo({ top: 0, behavior: "smooth" }); }}
        >
          <span className="unlock-toast-orb">{unlockToast === "fox" ? "🦊" : unlockToast === "traveler" ? "🧣" : "🪐"}</span>
          {unlockToast === "fox" ? (
            <span>
              <b>행성에 여우가 찾아왔어요</b>
              <span className="unlock-toast-sub">{FOX_UNLOCK}권을 읽은 행성에만 앉아요</span>
            </span>
          ) : unlockToast === "traveler" ? (
            <span>
              <b>행성에 여행자가 찾아왔어요</b>
              <span className="unlock-toast-sub">{TRAVELER_UNLOCK}권이 쌓인 행성에만 내려앉아요</span>
            </span>
          ) : (
            <span>
              <b>행성 색을 고를 수 있게 됐어요</b>
              <span className="unlock-toast-sub">프로필 편집에서 바꿔보세요 →</span>
            </span>
          )}
        </button>
      )}
      <FeedbackButton />
      {/* 피드백 바로 위. 게스트 화면에는 두지 않는다 — 거기는 '회원가입하고
          시작하기' 가 할 일이고, 밖으로 나가는 문을 나란히 두지 않는다. */}
      <InstagramButton />
      {/* 새 소식 — 좌측 하단 피드백과 대칭으로 우측 하단. 안 본 소식이 있으면 금색 점 */}
      <button className="news-fab" onClick={openNews}
        aria-label={newsUnseen ? "새 소식, 안 본 소식 있음" : "새 소식"}>
        <span className="news-fab-icon" aria-hidden="true">✦</span>
        <span className="news-fab-label">새 소식</span>
        {newsUnseen && <span className="news-dot" />}
      </button>
      {showConstellation && (
        <ConstellationModal
          books={books}
          uid={uid}
          name={getUserDisplayName(currentUser)}
          onClose={() => setShowConstellation(false)}
        />
      )}
      {showReading && (
        <ReadingModal
          books={incompleteBooks}
          uid={uid}
          onClose={() => setShowReading(false)}
          onBookClick={onBookClick}
        />
      )}
      {showNews && <ReleaseNotesModal onClose={() => setShowNews(false)} onGo={goNews} />}
      {showPti && (
        <BookPtiModal
          uid={uid}
          books={books}
          users={users}
          myType={(users || []).find((u) => u.id === uid)?.bookType || null}
          onSave={onSaveBookType}
          onClose={() => setShowPti(false)}
          onBookClick={(id) => { setShowPti(false); onBookClick(id); }}
          onUserClick={(id) => { setShowPti(false); onUserClick(id); }}
        />
      )}
      {showTrending && (
        <TrendingModal
          allBooks={allBooks}
          onClose={() => setShowTrending(false)}
          onBookClick={onBookClick}
        />
      )}
    </div>
  );
};

export default HomeScreen;
